// Blog post registry. Single source of truth for both the
// blog index page (/blogs/) and detail pages (/blogs/<slug>/),
// the BlogPosting JSON-LD emitted by `scripts/prerender.mjs`,
// the sitemap.xml extension, and any future RSS feed.
//
// Authoring flow:
//   1. Create `src/posts/<slug>.mdx`.
//   2. Set the YAML frontmatter (see `BlogPostFrontmatter`).
//   3. Write the body in MDX. JSX components imported at the
//      top of the .mdx are available; everything else parses
//      as GitHub-flavored markdown (tables, strikethrough,
//      autolinks via remark-gfm).
//
// At build time, Vite resolves the two `import.meta.glob` calls
// below eagerly: one for the MDX component + frontmatter pair,
// one for the raw source so reading-time is computed from the
// real content (not a hand-maintained frontmatter field that
// would drift). The validation block at module load fails the
// build loudly if a post's frontmatter is malformed.

import type { ComponentType } from 'react';

/**
 * The frontmatter shape authors write at the top of each .mdx.
 *
 * `slug` is intentionally required (not derived from the
 * filename) so the canonical URL is explicit in the source —
 * renames of the .mdx file don't accidentally change the URL.
 */
export interface BlogPostFrontmatter {
  readonly title: string;
  readonly description: string;
  readonly slug: string;
  /** ISO-8601 date the post was first published. Drives sort order on /blogs/. */
  readonly publishedAt: string;
  /** Optional ISO-8601 date of the last meaningful revision. */
  readonly updatedAt?: string;
  /** Topical tags surfaced as chips on the index + detail pages. */
  readonly tags?: ReadonlyArray<string>;
  /**
   * Optional per-post OG image URL (absolute path from the
   * landing root, e.g. `/og-images/<slug>.jpg`). Defaults to
   * the site-wide `/og.png`. Should be exactly 1200×630 pixels
   * — the prerender hardcodes those dimensions in the
   * `og:image:width/height` substitution.
   */
  readonly ogImage?: string;
  /**
   * Optional alt text for the per-post OG image. Defaults to
   * the post title if `ogImage` is set but `ogImageAlt` is not.
   * Surfaced as both `og:image:alt` and `twitter:image:alt`.
   */
  readonly ogImageAlt?: string;
  /**
   * Optional draft flag. Drafts are excluded from POSTS,
   * sitemap.xml, and the prerender route list — useful for
   * keeping a work-in-progress .mdx in the tree without
   * shipping it to production.
   */
  readonly draft?: boolean;
}

/**
 * The compiled, ready-to-render shape. `Component` is the
 * MDX-default export (the article body); `readingTime` is
 * computed from the raw source at build time, not authored.
 */
export interface Post {
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  readonly publishedAt: string;
  readonly updatedAt?: string;
  readonly tags: ReadonlyArray<string>;
  readonly readingTime: number;
  /**
   * Word count of the rendered body (after stripping frontmatter,
   * code blocks, JSX expressions, and HTML tags). Used by the
   * BlogPosting JSON-LD's `wordCount` and by build scripts that
   * surface posts to LLM crawlers.
   */
  readonly wordCount: number;
  readonly ogImage?: string;
  readonly ogImageAlt?: string;
  readonly Component: ComponentType;
}

// `eager: true` resolves the modules at build time so the
// caller is synchronous (no top-level await). Both globs share
// the same path pattern; Vite deduplicates the underlying file
// reads.
const moduleEntries = import.meta.glob<{
  default: ComponentType;
  frontmatter: Record<string, unknown>;
}>('./posts/*.mdx', { eager: true });

const rawEntries = import.meta.glob<string>('./posts/*.mdx', {
  eager: true,
  query: '?raw',
  import: 'default',
});

function asString(value: unknown, field: string, path: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`posts: frontmatter field "${field}" is missing or empty in ${path}`);
  }
  return value;
}

function asOptionalString(value: unknown, field: string, path: string): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `posts: frontmatter field "${field}" must be a non-empty string when present in ${path}`,
    );
  }
  return value;
}

function asStringArray(value: unknown, field: string, path: string): ReadonlyArray<string> {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || value.some((v) => typeof v !== 'string')) {
    throw new Error(
      `posts: frontmatter field "${field}" must be a string[] when present in ${path}`,
    );
  }
  return value as ReadonlyArray<string>;
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

/**
 * Strip frontmatter, fenced code, inline code, and MDX
 * expressions from raw .mdx source. Used as the first pass
 * before counting word tokens — keeps the source's prose
 * shape without trying to mimic an HTML sanitizer.
 */
function stripToProse(source: string): string {
  return source
    .replace(/^---[\s\S]*?---\s*/, '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`[^`]*`/g, '')
    .replace(/\{[^}]*\}/g, '');
}

/**
 * Count word-like tokens via `\b\w+\b` rather than stripping
 * HTML-tag-shaped regions and splitting on whitespace.
 *
 * The previous shape (`replace(/<[^>]*>/g, '')` then split) was
 * functionally correct for our purposes — the output here only
 * ever drives reading-time arithmetic and a JSON-LD `wordCount`
 * field, never gets rendered as HTML — but it patterned as an
 * "incomplete HTML sanitizer" to CodeQL's
 * `js/incomplete-multi-character-sanitization` rule (which
 * flags HTML-stripping regexes that can be bypassed by
 * malformed input like `<script alert(1)` with no closing `>`).
 *
 * Token-matching directly avoids the rule entirely and is
 * morally identical for word-counting. JSX tag identifiers
 * (e.g. `CodeBlock` in `<CodeBlock />`) now contribute one word
 * each, which slightly inflates totals for components-heavy
 * posts — bounded to ~1–2% on a typical 1500-word post and
 * well under the reading-time rounding floor.
 */
function countWords(source: string): number {
  return stripToProse(source).match(/\b\w+\b/g)?.length ?? 0;
}

/**
 * Word-count → reading-time. 220 wpm is the midpoint of
 * published reading-speed ranges for technical content.
 */
function readingTimeMinutes(words: number): number {
  return Math.max(1, Math.round(words / 220));
}

function compile(path: string): Post | null {
  const mod = moduleEntries[path];
  const raw = rawEntries[path];
  if (mod === undefined || raw === undefined) {
    throw new Error(`posts: module entry missing for ${path}`);
  }

  // Cast the raw `Record<string, unknown>` frontmatter to a
  // shape with explicitly-declared optional keys. With this
  // shape (no index signature), dot access compiles cleanly
  // under TS's `noPropertyAccessFromIndexSignature` AND
  // satisfies Biome's `useLiteralKeys` rule (the previous
  // bracket-access form failed Biome's check). Each field stays
  // typed as `unknown` so the validation helpers still do all
  // the real runtime checking — this cast is purely a syntactic
  // accommodation, not a runtime type assertion.
  const fm = mod.frontmatter as {
    title?: unknown;
    description?: unknown;
    slug?: unknown;
    publishedAt?: unknown;
    updatedAt?: unknown;
    tags?: unknown;
    ogImage?: unknown;
    ogImageAlt?: unknown;
    draft?: unknown;
  };
  if (asBoolean(fm.draft)) return null;

  const title = asString(fm.title, 'title', path);
  const description = asString(fm.description, 'description', path);
  const slug = asString(fm.slug, 'slug', path);
  const publishedAt = asString(fm.publishedAt, 'publishedAt', path);
  const updatedAt = asOptionalString(fm.updatedAt, 'updatedAt', path);
  const tags = asStringArray(fm.tags, 'tags', path);
  const ogImage = asOptionalString(fm.ogImage, 'ogImage', path);
  const ogImageAlt = asOptionalString(fm.ogImageAlt, 'ogImageAlt', path);

  if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug)) {
    throw new Error(
      `posts: slug "${slug}" in ${path} must be kebab-case lowercase (matches /^[a-z0-9][a-z0-9-]*[a-z0-9]$/)`,
    );
  }
  if (Number.isNaN(Date.parse(publishedAt))) {
    throw new Error(`posts: publishedAt "${publishedAt}" in ${path} is not a parseable date`);
  }
  if (updatedAt !== undefined && Number.isNaN(Date.parse(updatedAt))) {
    throw new Error(`posts: updatedAt "${updatedAt}" in ${path} is not a parseable date`);
  }

  const wordCount = countWords(raw);

  // Spread optionals conditionally so `exactOptionalPropertyTypes`
  // doesn't see the field present with `undefined` — the type
  // declares them as omittable, not nullable.
  return {
    slug,
    title,
    description,
    publishedAt,
    tags,
    readingTime: readingTimeMinutes(wordCount),
    wordCount,
    Component: mod.default,
    ...(updatedAt !== undefined ? { updatedAt } : {}),
    ...(ogImage !== undefined ? { ogImage } : {}),
    ...(ogImageAlt !== undefined ? { ogImageAlt } : {}),
  };
}

const compiled: Post[] = [];
const slugs = new Set<string>();
for (const path of Object.keys(moduleEntries).sort()) {
  const post = compile(path);
  if (post === null) continue;
  if (slugs.has(post.slug)) {
    throw new Error(`posts: duplicate slug "${post.slug}" detected (from ${path})`);
  }
  slugs.add(post.slug);
  compiled.push(post);
}
compiled.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));

/**
 * All published (non-draft) posts, newest first. Stable across
 * client and server bundles — Vite resolves `import.meta.glob`
 * the same way in both.
 */
export const POSTS: ReadonlyArray<Post> = compiled;

export function getPost(slug: string): Post | undefined {
  return compiled.find((p) => p.slug === slug);
}
